import axios from "axios";
import { BlogPost, BlogComment } from "../types/Blog";
import { getAuth } from "firebase/auth";

let API_BASE_URL = "";

if (process.env.NODE_ENV === "production") {
  API_BASE_URL = process.env.REACT_APP_BLOG_API_URL_PRODUCTION || "";
} else {
  API_BASE_URL = process.env.REACT_APP_BLOG_API_URL_DEVELOPMENT || "";
}

const createHeaders = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated");
  }
  const token = await user.getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const extractArray = (data: unknown): BlogPost[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.posts)) return obj.posts;
    if (Array.isArray(obj.data)) return obj.data;
    if (Array.isArray(obj.items)) return obj.items;
  }
  return [];
};

export const blogApi = {
  // Public
  getAllPublished: async (): Promise<BlogPost[]> => {
    const res = await axios.get(`${API_BASE_URL}/api/blog`);
    return extractArray(res.data);
  },

  getBySlug: async (slug: string): Promise<BlogPost> => {
    const res = await axios.get(`${API_BASE_URL}/api/blog/${slug}`);
    return res.data;
  },

  // Admin
  getAll: async (): Promise<BlogPost[]> => {
    const headers = await createHeaders();
    const res = await axios.get(`${API_BASE_URL}/api/blog/all`, { headers });
    return extractArray(res.data);
  },

  getById: async (id: string): Promise<BlogPost> => {
    const headers = await createHeaders();
    const res = await axios.get(`${API_BASE_URL}/api/blog/id/${id}`, { headers });
    return res.data;
  },

  create: async (post: Omit<BlogPost, "id" | "slug" | "createdAt" | "updatedAt">): Promise<BlogPost> => {
    const headers = await createHeaders();
    const res = await axios.post(`${API_BASE_URL}/api/blog`, post, { headers });
    return res.data;
  },

  update: async (id: string, post: Partial<BlogPost>): Promise<BlogPost> => {
    const headers = await createHeaders();
    const res = await axios.put(`${API_BASE_URL}/api/blog/${id}`, post, { headers });
    return res.data;
  },

  delete: async (id: string): Promise<void> => {
    const headers = await createHeaders();
    await axios.delete(`${API_BASE_URL}/api/blog/${id}`, { headers });
  },

  // Comments
  getComments: async (postId: string): Promise<BlogComment[]> => {
    const res = await axios.get(`${API_BASE_URL}/api/blog/${postId}/comments`);
    return res.data;
  },

  addComment: async (postId: string, content: string): Promise<BlogComment> => {
    const headers = await createHeaders();
    const res = await axios.post(
      `${API_BASE_URL}/api/blog/${postId}/comments`,
      { content },
      { headers }
    );
    return res.data;
  },

  deleteComment: async (postId: string, commentId: string): Promise<void> => {
    const headers = await createHeaders();
    await axios.delete(`${API_BASE_URL}/api/blog/${postId}/comments/${commentId}`, { headers });
  },
};
